import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Button, BoxV2 as Box, Flex, Table, useTable } from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey } from '@fortawesome/pro-regular-svg-icons';

import ApiKeyCreatedModal from './ApiKeyCreatedModal';
import ApiKeyListingRow from './ApiKeyListingRow';
import { globalGetService, globalPostService } from '@/utils/globalApiServices';
import { useAuthContext } from '@/contexts';
import { DATADOG_ENV } from '@/constants';

const TABLE_COLUMNS = [
  { id: 'label', label: 'API LABEL', sortable: false },
  { id: 'key', label: 'API KEY', sortable: false },
  { id: 'last_updated', label: 'LAST UPDATED', sortable: false },
  { id: 'active', label: 'ACTIVE', sortable: false },
  { id: 'actions', label: 'ACTIONS', sortable: false },
];

const PAGE_SIZE = 10;
const IS_PROD_ENV = DATADOG_ENV === 'prod';
const GLOBAL_MAX_API_KEYS = IS_PROD_ENV ? 5 : 15;

export default function ApiKeysListing() {
  const { profile } = useAuthContext();
  const [addNewApiKey, setAddNewApiKey] = useState(false);
  const [activeAccount, setActiveAccount] = useState({});
  const [isAddingNewKey, setIsAddingNewKey] = useState(false);
  const skipFetchRef = useRef(false);
  const maxApiKeys = profile?.maxApiKeys ?? GLOBAL_MAX_API_KEYS;

  const table = useTable(
    async (filter) => {
      // only fetch list when mounted or performed action
      if (skipFetchRef.current) {
        return {
          data: table.tableData,
          total: table.total,
          rowsPerPage: PAGE_SIZE,
          page: filter.page || 1,
        };
      }
      const response = await globalGetService(`csp/api/account`).then(
        (response) => (response?.ok ? response.data : [])
      );
      skipFetchRef.current = true;

      return {
        data: response,
        total: response.length,
        rowsPerPage: PAGE_SIZE,
        page: filter.page || 1,
      };
    },
    { deps: [], inMemory: true }
  );

  const isMaxKeysReached = (table.total || 0) >= maxApiKeys;

  const reloadTable = (filter) => {
    skipFetchRef.current = false;
    table.reload(filter);
  };

  const handleAddNewApiKey = () => {
    setIsAddingNewKey(true);
    globalPostService(`csp/api/account`, {}).then((response) => {
      setIsAddingNewKey(false);
      if (response?.ok) {
        setActiveAccount(response.data);
        setAddNewApiKey(true);
        reloadTable();
      }
    });
  };

  const displayAccounts = useMemo(() => {
    const start = (table.filter.page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return table.tableData.slice(start, end);
  }, [table.tableData, table.filter]);

  useEffect(() => {
    if (displayAccounts.length === 0 && table.tableData.length > 0) {
      reloadTable({ page: 1 });
    }
  }, [table.tableData.length, displayAccounts.length]);

  return (
    <>
      <div className="api-keys-listing-wrapper" data-testid="apiKeysListing">
        <div className="title-blk">
          <h3 className="flex heading1 space-between v-center">
            <Flex sx={{ alignItems: 'center' }}>
              <Box sx={{ mr: 'xs' }}>
                <FontAwesomeIcon icon={faKey} />
              </Box>
              <span>API Keys</span>
            </Flex>
            <Button
              data-testid="apiKeysListingAddButton"
              onClick={handleAddNewApiKey}
              disabled={isAddingNewKey || isMaxKeysReached}
              shape="square"
              size="small"
            >
              Create API Key
            </Button>
          </h3>
        </div>
        <Table
          testid="apiKeyListingTable"
          loading={table.loading}
          className="api-keys-listing-table"
          emptyState=""
          disableHover
          headRows={TABLE_COLUMNS}
          data={displayAccounts}
          renderRow={(data) => {
            return (
              <ApiKeyListingRow
                accountObj={data}
                key={data.id}
                fetchApiAccounts={reloadTable}
              />
            );
          }}
          handleChangePage={table.handleChangePage}
          pagination={table.pagination}
        />
        {isMaxKeysReached && (
          <Box
            sx={{
              mt: 'l',
              textAlign: 'center',
              fontSize: 's',
              fontWeight: 400,
            }}
          >
            Maximum number of API keys reached!
            <br />
            Please delete an existing API key if you wish to create a new one.
          </Box>
        )}
      </div>
      <ApiKeyCreatedModal
        open={addNewApiKey}
        account={activeAccount}
        onClose={() => {
          setAddNewApiKey(false);
          setActiveAccount({});
        }}
      />
    </>
  );
}
